/**
 * Attaching a registered customer to a sale — optional, and rarely used.
 *
 * Most sales are a walk-in with nothing more than a name typed for the receipt (`customerChanged`,
 * not this sheet). This exists for the minority that matters: a credit account, a loyalty member,
 * a known wholesale buyer — someone the owner wants to be able to find again by name later. Search
 * first; "new customer" is one tap away for the rare first-time case, never the default path.
 */

import { useState } from 'react';

import { useCreateCustomerMutation, useSearchCustomersQuery, type Customer } from '@pos/shared';

import { Button, EmptyState, ErrorState, Field, Sheet, SkeletonRows } from '@/shared/ui';

export type CustomerPickerSheetProps = {
  open: boolean;
  onPick: (customer: Customer) => void;
  onDismiss: () => void;
};

export function CustomerPickerSheet({ open, onPick, onDismiss }: CustomerPickerSheetProps) {
  const [mode, setMode] = useState<'search' | 'create'>('search');
  const [term, setTerm] = useState('');
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');

  const needle = term.trim();
  const { data, isFetching, error, refetch } = useSearchCustomersQuery(
    { term: needle },
    { skip: !open || mode !== 'search' || needle.length < 2 },
  );

  const [createCustomer, { isLoading: isCreating }] = useCreateCustomerMutation();

  const reset = () => {
    setMode('search');
    setTerm('');
    setNewName('');
    setNewPhone('');
  };

  return (
    <Sheet
      open={open}
      onDismiss={() => {
        reset();
        onDismiss();
      }}
      title={mode === 'create' ? 'New customer' : 'Attach a customer'}
    >
      {mode === 'search' ? (
        <>
          <Field
            label="Search by name or phone"
            placeholder="Type at least 2 characters"
            value={term}
            autoFocus
            onChange={(event) => {
              setTerm(event.target.value);
            }}
          />

          <div className="mt-4">
            {needle.length < 2 ? (
              <p className="text-sm text-content-muted">
                Keep typing to search existing customers.
              </p>
            ) : isFetching ? (
              <SkeletonRows rows={3} />
            ) : error !== undefined ? (
              <ErrorState
                title="The customer list could not be loaded"
                body="Check the connection and try again."
                onRetry={() => void refetch()}
              />
            ) : (data?.length ?? 0) === 0 ? (
              <EmptyState title="No match" body="Nobody registered matches that yet." />
            ) : (
              <ul className="flex flex-col gap-2">
                {(data ?? []).map((customer) => (
                  <li key={customer.id}>
                    <Button
                      variant="quiet"
                      fullWidth
                      className="justify-start"
                      onClick={() => {
                        reset();
                        onPick(customer);
                      }}
                    >
                      <span className="flex flex-col items-start">
                        <span className="font-semibold">{customer.name}</span>
                        {customer.phone !== null && customer.phone !== undefined && (
                          <span className="text-xs text-content-muted">{customer.phone}</span>
                        )}
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-5 border-t border-line-subtle pt-4">
            <Button
              variant="ghost"
              fullWidth
              onClick={() => {
                setNewName(term);
                setMode('create');
              }}
            >
              + New customer
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-4">
          <Field
            label="Name"
            placeholder="Required"
            value={newName}
            autoFocus
            onChange={(event) => {
              setNewName(event.target.value);
            }}
          />

          <Field
            label="Phone"
            placeholder="Optional"
            inputMode="tel"
            value={newPhone}
            onChange={(event) => {
              setNewPhone(event.target.value);
            }}
          />

          <Button
            size="lg"
            fullWidth
            loading={isCreating}
            disabled={newName.trim().length === 0}
            onClick={() => {
              void (async () => {
                const trimmedName = newName.trim();
                const trimmedPhone = newPhone.trim();

                const id = await createCustomer({
                  name: trimmedName,
                  ...(trimmedPhone.length > 0 && { phone: trimmedPhone }),
                }).unwrap();

                reset();
                onPick({
                  id,
                  name: trimmedName,
                  phone: trimmedPhone.length > 0 ? trimmedPhone : null,
                  isActive: true,
                });
              })();
            }}
          >
            Save and attach
          </Button>

          <Button
            variant="quiet"
            fullWidth
            onClick={() => {
              setMode('search');
            }}
          >
            Back to search
          </Button>
        </div>
      )}
    </Sheet>
  );
}
